import { WeekNav } from '@wundo/ui';
import type { MeetingBucket } from '@wundo/shared/calendar.ts';
import { NoCalendarEmptyState } from '@/components/NoCalendarEmptyState.tsx';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { addDays } from '@/lib/week.ts';
import { m } from '@/paraglide/messages.js';
import type { AgendaView } from './AgendaToolbar.tsx';
import { AgendaListView } from './AgendaListView.tsx';
import { AgendaWeekView } from './AgendaWeekView.tsx';
import { passesFilter } from './agendaFilter.ts';
import { formatWeekRange } from './meetingFormat.ts';
import type { AgendaContent } from './useAgendaMeetings.ts';
import type { AgendaWeek } from './useAgendaWeek.ts';

const AGENDA_LAYOUTS = {
  week: AgendaWeekView,
  list: AgendaListView,
} satisfies Record<AgendaView, unknown>;

export interface AgendaBodyProps {
  content: AgendaContent;
  view: AgendaView;
  week: AgendaWeek;
  buckets: readonly MeetingBucket[];
  onSelect: (meeting: CalendarMeetingItem) => void;
  onLink: (meeting: CalendarMeetingItem) => void;
}

/** Everything under the agenda's toolbar: the week, the empty state, or why neither is there yet. */
export function AgendaBody({
  content,
  view,
  week,
  buckets,
  onSelect,
  onLink,
}: AgendaBodyProps) {
  if (content.kind === 'loading') {
    return <p className='text-base text-ink-muted'>{m.agenda_loading()}</p>;
  }
  if (content.kind === 'failed') {
    return <p className='text-base text-danger'>{m.agenda_error()}</p>;
  }

  const AgendaLayout = AGENDA_LAYOUTS[view];

  return (
    <>
      {/* Only the grid gets week arrows: the list already names each day it shows. */}
      {view === 'week' && (
        <WeekNav
          label={formatWeekRange(
            week.weekStart,
            addDays(week.weekStart, week.visibleDays - 1),
          )}
          previousLabel={m.agenda_previous_week()}
          nextLabel={m.agenda_next_week()}
          onPrevious={week.showPreviousWeek}
          onNext={week.showNextWeek}
        />
      )}
      {content.kind === 'nothingAtAll' ? (
        <NoCalendarEmptyState
          className='mt-4'
          userId={content.userId}
          title={m.agenda_empty_title()}
          description={m.agenda_empty_body()}
        />
      ) : (
        <AgendaLayout
          days={week.days}
          meetings={content.meetings.filter((meeting) => passesFilter(meeting, buckets))}
          onSelect={onSelect}
          onLink={onLink}
        />
      )}
    </>
  );
}
